import { useEffect, useMemo } from 'react'
import { UseFormSetError, FieldValues, Path } from 'react-hook-form'

interface IValidationError {
  propertyName: string
  errorMessage: string
}

/**
 * Custom hook to parse backend validation errors from FluentValidation.
 * Separates field-level errors (with propertyName) from general errors.
 * Field errors are automatically set on the form using setError.
 *
 * @param errorMessage - The raw error message string from the API response
 * @param setError - The setError function from react-hook-form
 * @returns Object containing generalError string (field errors go into formState)
 */
export function useServerValidation<T extends FieldValues>(
  errorMessage: string | undefined | null,
  setError: UseFormSetError<T>
): { generalError: string | null } {
  const { fieldErrors, generalError } = useMemo(() => {
    if (!errorMessage) {
      return { fieldErrors: [] as IValidationError[], generalError: null }
    }

    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed)) {
        const fieldErrors = parsed.filter(
          (err: IValidationError) => err.propertyName && err.errorMessage
        )
        const generalErrors = parsed.filter(
          (err: IValidationError) => !err.propertyName && err.errorMessage
        )

        return {
          fieldErrors,
          generalError:
            generalErrors.length > 0
              ? generalErrors
                  .map((e: IValidationError) => e.errorMessage)
                  .join(', ')
              : null,
        }
      }
    } catch {
      // Not JSON, treat as general error
    }

    return { fieldErrors: [] as IValidationError[], generalError: errorMessage }
  }, [errorMessage])

  // Set field errors on the form when they change
  useEffect(() => {
    fieldErrors.forEach(err => {
      setError(err.propertyName as Path<T>, {
        type: 'server',
        message: err.errorMessage,
      })
    })
  }, [fieldErrors, setError])

  return { generalError }
}
